/**
 * Zod-Schemas und reine Rechenfunktionen der Projektprotokolle (ADR 0084).
 *
 * Das Schema für die Formularfelder wird **aus der Vorlage abgeleitet** — eine
 * zweite, handgepflegte Feldliste würde binnen weniger Änderungen von der
 * Vorlage abweichen (genau der Fehler, den ADR 0084 an den Word-Dateien
 * beschreibt).
 */
import { z } from 'zod';
import {
  CHECK_RESULTS,
  PROTOCOL_LANGUAGES,
  PROTOCOL_STATUSES,
  PROTOCOL_TYPES,
  type Bilingual,
  type CheckResult,
  type ProtocolField,
  type ProtocolTemplate,
  type ProtocolType,
} from './project-protocol.types';
import { protocolTemplate } from './project-protocol.templates';

export const protocolTypeSchema = z.enum(PROTOCOL_TYPES);
export const protocolStatusSchema = z.enum(PROTOCOL_STATUSES);
export const protocolLanguageSchema = z.enum(PROTOCOL_LANGUAGES);
export const checkResultSchema = z.enum(CHECK_RESULTS);

/** Deutsche Kurzlabel für Listen und Filter (die UI ist deutsch, nicht das Dokument). */
export const PROTOCOL_TYPE_LABELS: Record<ProtocolType, string> = {
  PLANFREIGABE: 'Planfreigabe',
  BAUSTELLENVORBEREITUNG: 'Baustellenvorbereitung',
  PROBELAUF: 'Probelauf',
  ABNAHME: 'Abnahme & Übergabe',
};

export const PROTOCOL_STATUS_LABELS: Record<(typeof PROTOCOL_STATUSES)[number], string> = {
  ENTWURF: 'Entwurf',
  VERSENDET: 'Zur Unterschrift versendet',
  UNTERSCHRIEBEN: 'Unterschrieben',
  VERWORFEN: 'Verworfen',
};

/** Ein Feld → sein Zod-Typ. Alles optional: das Formular speichert Feld für Feld (Auto-Save). */
function fieldSchema(field: ProtocolField): z.ZodTypeAny {
  switch (field.type) {
    case 'number':
      return z.number().finite().nullable();
    case 'bool':
      return z.boolean().nullable();
    case 'date':
      return z.string().datetime().nullable();
    case 'enum':
      return z
        .enum((field.options ?? []).map((option) => option.value) as [string, ...string[]])
        .nullable();
    case 'longtext':
      return z.string().max(5000).nullable();
    default:
      return z.string().max(500).nullable();
  }
}

/** Alle Felder einer Vorlage, über die Abschnitte hinweg. */
export function templateFields(template: ProtocolTemplate): readonly ProtocolField[] {
  return template.sections.flatMap((section) => section.fields);
}

/**
 * Aus der Vorlage abgeleitetes Schema der Formularfelder. `.partial()`, weil das
 * Formular jedes Feld einzeln schickt (Muster `assembly-report`, ADR 0070 §7).
 */
export function protocolDataSchema(type: ProtocolType): z.ZodObject<z.ZodRawShape> {
  const shape: z.ZodRawShape = {};
  for (const field of templateFields(protocolTemplate(type))) {
    shape[field.key] = fieldSchema(field).optional();
  }
  return z.object(shape).strict();
}

export const createProjectProtocolSchema = z.object({
  type: protocolTypeSchema,
  /** Pflicht für Vorlagen mit `needsAssemblyTask`; der Server prüft das. */
  assemblyTaskId: z.string().uuid().nullable().optional(),
  /** Ohne Angabe die Projektsprache, sonst DE. */
  language: protocolLanguageSchema.optional(),
  /** Ausstellende Gesellschaft; ohne Angabe die Owner-Gesellschaft des Projekts. */
  legalEntityId: z.string().uuid().nullable().optional(),
});
export type CreateProjectProtocolInput = z.infer<typeof createProjectProtocolSchema>;

export const updateProjectProtocolSchema = z.object({
  /** Teilmenge der Vorlagenfelder — gegen `protocolDataSchema(type)` geprüft. */
  data: z.record(z.unknown()).optional(),
  language: protocolLanguageSchema.optional(),
  legalEntityId: z.string().uuid().nullable().optional(),
  optionalBlocks: z.array(z.string().max(40)).max(20).optional(),
  recipientEmail: z.string().email().nullable().optional(),
});
export type UpdateProjectProtocolInput = z.infer<typeof updateProjectProtocolSchema>;

export const updateProtocolCheckItemSchema = z.object({
  result: checkResultSchema.nullable().optional(),
  defect: z.string().max(1000).nullable().optional(),
  measure: z.string().max(1000).nullable().optional(),
});
export type UpdateProtocolCheckItemInput = z.infer<typeof updateProtocolCheckItemSchema>;

/**
 * Rücklauf vermerken (ADR 0084 §4, überarbeitet 2026-08-06). Es gibt keine
 * Unterschrift am Bildschirm mehr: Das Protokoll geht per Mail hinaus, der
 * Kunde unterschreibt auf Papier und schickt den Scan zurück. Vermerkt werden
 * Datum und wer unterschrieben hat; der Scan selbst kommt als Datei-Upload
 * (Rohdaten im Body, Muster wie die Meldungs-Screenshots).
 */
export const markProtocolReturnedSchema = z.object({
  /** Wer unterschrieben hat — steht auf dem Scan, hier nur zur Anzeige. */
  signerName: z.string().trim().max(120).nullish(),
  /** Datum der Unterschrift laut Scan; leer = heute. */
  signedAt: z.string().datetime().nullish(),
  note: z.string().trim().max(500).nullish(),
});
export type MarkProtocolReturnedInput = z.infer<typeof markProtocolReturnedSchema>;

/** Metadaten des hochgeladenen Rücklauf-Scans (als Query, Bytes im Body). */
export const uploadProtocolReturnSchema = z.object({
  fileName: z.string().trim().min(1).max(200),
  signerName: z.string().trim().max(120).optional(),
  signedAt: z.string().datetime().optional(),
  note: z.string().trim().max(500).optional(),
});
export type UploadProtocolReturnInput = z.infer<typeof uploadProtocolReturnSchema>;

/** Rücklauf-Scans sind PDFs oder Fotos — nichts Skriptbares (kein SVG, kein HTML). */
export const PROTOCOL_RETURN_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/tiff',
] as const;
export type ProtocolReturnMimeType = (typeof PROTOCOL_RETURN_MIME_TYPES)[number];
export const PROTOCOL_RETURN_MAX_BYTES = 25 * 1024 * 1024;

export const protocolCheckItemResponseSchema = z.object({
  id: z.string().uuid(),
  groupKey: z.string(),
  key: z.string(),
  position: z.number().int(),
  result: checkResultSchema.nullable(),
  defect: z.string().nullable(),
  measure: z.string().nullable(),
  /** Erzeugter Eintrag am Baustellenbericht (ADR 0084 §3), falls vorhanden. */
  reportItemId: z.string().uuid().nullable(),
});
export type ProtocolCheckItemResponse = z.infer<typeof protocolCheckItemResponseSchema>;


export const projectProtocolResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  projectId: z.string().uuid(),
  projectCode: z.string().nullable(),
  assemblyTaskId: z.string().uuid().nullable(),
  type: protocolTypeSchema,
  status: protocolStatusSchema,
  language: protocolLanguageSchema,
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  templateRevision: z.string(),
  data: z.record(z.unknown()),
  optionalBlocks: z.array(z.string()),
  recipientEmail: z.string().nullable(),
  sentAt: z.string().datetime().nullable(),
  signedAt: z.string().datetime().nullable(),
  /** Wer laut Rücklauf-Scan unterschrieben hat (ADR 0084 §4). */
  signerName: z.string().nullable(),
  /** Dateiname des hochgeladenen Rücklauf-Scans; null = keiner hinterlegt. */
  returnedFileName: z.string().nullable(),
  returnedNote: z.string().nullable(),
  checkItems: z.array(protocolCheckItemResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  createdBy: z.string().uuid(),
});
export type ProjectProtocolResponse = z.infer<typeof projectProtocolResponseSchema>;

// ── Reine Funktionen ─────────────────────────────────────────────────────────

export interface FlatCheckItem {
  groupKey: string;
  key: string;
  position: number;
  text: Bilingual;
}

/**
 * Prüfpunkte einer Vorlage in Druckreihenfolge — dieselbe Reihenfolge für das
 * Anlegen der Zeilen, das Formular und das PDF.
 */
export function flattenCheckItems(template: ProtocolTemplate): FlatCheckItem[] {
  const flat: FlatCheckItem[] = [];
  let position = 1;
  for (const group of template.checkGroups ?? []) {
    for (const item of group.items) {
      flat.push({ groupKey: group.key, key: item.key, position: position++, text: item.text });
    }
  }
  return flat;
}

/**
 * Gewährleistungsende = Übergabedatum + `years` Jahre (ADR 0084 §3).
 *
 * Der 29. Februar hat in einem Nicht-Schaltjahr kein Gegenstück — `setFullYear`
 * würde daraus stillschweigend den 1. März machen. Für eine Frist, die einen
 * Anspruch beendet, ist der **letzte Tag des Vormonats-Pendants** (28.02.) die
 * übliche und für den Auftraggeber günstigere Auslegung.
 */
export function computeWarrantyEnd(handoverIso: string | null, years: number | null): string | null {
  if (!handoverIso || years == null || !Number.isFinite(years) || years <= 0) return null;
  const start = new Date(handoverIso);
  if (Number.isNaN(start.getTime())) return null;
  const end = new Date(start);
  const day = start.getUTCDate();
  end.setUTCFullYear(start.getUTCFullYear() + Math.trunc(years));
  // Übergelaufen (29.02. → 01.03.)? Auf den letzten Tag des Zielmonats ziehen.
  if (end.getUTCDate() !== day) end.setUTCDate(0);
  return end.toISOString();
}

/**
 * Ausfüllstand: Anteil beantworteter Prüfpunkte bzw. befüllter Felder. Rein für
 * die Anzeige — nichts blockiert daran.
 */
export function protocolProgress(params: {
  template: ProtocolTemplate;
  data: Record<string, unknown>;
  checkItems: readonly { result: CheckResult | null }[];
}): { done: number; total: number; percent: number } {
  const fields = templateFields(params.template).filter((field) => !field.isDerived);
  const filled = fields.filter((field) => {
    const value = params.data[field.key];
    return value !== undefined && value !== null && value !== '';
  }).length;
  const answered = params.checkItems.filter((item) => item.result != null).length;

  const total = fields.length + params.checkItems.length;
  const done = filled + answered;
  return { done, total, percent: total === 0 ? 0 : Math.round((done / total) * 100) };
}

/**
 * Darf das Protokoll noch geändert werden? Ein unterschriebenes Dokument ist
 * eingefroren (ADR 0084 §5) — sonst wäre die Unterschrift wertlos.
 */
export function isProtocolEditable(status: (typeof PROTOCOL_STATUSES)[number]): boolean {
  return status === 'ENTWURF' || status === 'VERSENDET';
}
